import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import _ from "lodash";

import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { GripVerticalIcon, PlusIcon, Trash2Icon } from "lucide-react";
import { TagColorEnum } from "@/__generated__/graphql";

import {
  FieldDefinitionFieldType,
  FieldDefinitionTagType,
  FieldDefinitionTagsType,
  FieldImperativeHandle,
} from "@/components/@types";
import { Icon } from "@/components/atoms/Icon";
import { Popper } from "@/components/atoms/Popper";
import { TagColors, Values } from "@/constants/values";
import { useColorScheme } from "@/hooks/useColorScheme";
import { useContextScope } from "@/hooks/useContextTree";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { typographyStyles } from "@/components/ui/typography";

const GRID_COLS = 4;

function ColorGrid({
  onChange,
  selectedColor,
}: {
  onChange: (color: TagColorEnum) => void;
  selectedColor?: TagColorEnum;
}) {
  const { colorScheme: mode } = useColorScheme();
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const entries = Object.entries(TagColorEnum);

  const handleGridKeyDown = useCallback(
    (e: React.KeyboardEvent, idx: number) => {
      let next = idx;
      if (e.key === "ArrowRight") {
        next = idx + 1 < entries.length ? idx + 1 : 0;
      } else if (e.key === "ArrowLeft") {
        next = idx - 1 >= 0 ? idx - 1 : entries.length - 1;
      } else if (e.key === "ArrowDown") {
        next =
          idx + GRID_COLS < entries.length ? idx + GRID_COLS : idx % GRID_COLS;
      } else if (e.key === "ArrowUp") {
        next =
          idx - GRID_COLS >= 0
            ? idx - GRID_COLS
            : entries.length - GRID_COLS + (idx % GRID_COLS);
      } else if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onChange(entries[idx][1] as TagColorEnum);
        return;
      } else {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      itemRefs.current[next]?.focus();
    },
    [entries, onChange],
  );

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${GRID_COLS}, 1fr)`,
        gap: "8px",
        padding: "8px",
        width: "fit-content",
      }}
      data-testid="field-tag-color-grid"
    >
      {entries.map(([key, value], idx) => (
        <button
          key={key}
          ref={(el) => {
            itemRefs.current[idx] = el;
          }}
          className="field-tag-color-swatch"
          onClick={() => onChange(value as TagColorEnum)}
          onKeyDown={(e) => handleGridKeyDown(e, idx)}
          data-testid={`field-tag-color-${key}`}
          style={{
            width: 24,
            height: 24,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            border: selectedColor === value ? "2px solid" : "1px solid",
            padding: 0,
            background: "none",
            ...TagColors?.[value as TagColorEnum]?.[mode],
          }}
        >
          A
        </button>
      ))}
    </div>
  );
}

function SortableTag({
  tag,
  index,
  onColorChange,
  onDelete,
  onNameChange,
  onArrowUp,
  onArrowDown,
  navRef,
  events,
}: {
  tag: any;
  index: number;
  onColorChange: (color: TagColorEnum, index: number) => void;
  onDelete: (index: number) => void;
  onNameChange: (name: string, index: number) => void;
  onArrowUp: () => void;
  onArrowDown: () => void;
  navRef: (el: HTMLDivElement | null) => void;
  events: any;
}) {
  const { colorScheme: mode } = useColorScheme();
  const [isColorPopperOpen, setIsColorPopperOpen] = useState(false);
  const colorButtonRef = useRef<HTMLButtonElement>(null);
  const rowNavRef = useRef<HTMLDivElement>(null);
  const [isEditing, setIsEditing] = useState(false);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: tag.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const focusNav = useCallback(() => {
    setIsEditing(false);
    rowNavRef.current?.focus();
  }, []);

  const { Scope: ContentScope } = useContextScope({
    onEscape: focusNav,
    trapFocus: true,
  });

  const handleNavKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        e.stopPropagation();
        setIsEditing(true);
        requestAnimationFrame(() => colorButtonRef.current?.focus());
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        e.stopPropagation();
        onArrowUp();
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        e.stopPropagation();
        onArrowDown();
      } else if (e.key === "Delete" || e.key === "Backspace") {
        e.preventDefault();
        e.stopPropagation();
        onDelete(index);
      }
    },
    [onArrowUp, onArrowDown, onDelete, index],
  );

  const handleNavMouseDown = useCallback((e: React.MouseEvent) => {
    if (document.activeElement === rowNavRef.current) {
      e.preventDefault();
      e.stopPropagation();
      setIsEditing(true);
      requestAnimationFrame(() => colorButtonRef.current?.focus());
    }
  }, []);

  const setSortableRef = useCallback(
    (el: HTMLDivElement | null) => {
      setNodeRef(el);
    },
    [setNodeRef],
  );

  const setRowNavRef = useCallback(
    (el: HTMLDivElement | null) => {
      rowNavRef.current = el;
      navRef(el);
    },
    [navRef],
  );

  return (
    <div
      ref={setSortableRef}
      data-testid={`field-tag-sortable-${tag.name || "unnamed"}`}
      style={{
        ...style,
        opacity: isDragging ? 0.5 : 1,
        borderRadius: "calc(var(--radius) - 4px)",
        backgroundColor: "var(--background)",
        border: `1px solid ${isEditing ? "var(--ring)" : "var(--border)"}`,
        marginBottom: "4px",
        position: "relative",
      }}
    >
      {/* Nav layer — receives ArrowUp/Down and Enter */}
      <div
        ref={setRowNavRef}
        className="field-tag-nav"
        tabIndex={0}
        onKeyDown={handleNavKeyDown}
        onMouseDown={handleNavMouseDown}
        data-testid={`field-tag-nav-${tag.name || "unnamed"}`}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          zIndex: isEditing ? -1 : 1,
          borderRadius: "calc(var(--radius) - 4px)",
        }}
      />

      {/* Content layer — wrapped in context tree scope so Escape → focusNav */}
      <ContentScope
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          padding: "8px",
          position: "relative",
          zIndex: isEditing ? 1 : 0,
          pointerEvents: isEditing ? "auto" : "none",
        }}
      >
        <div
          {...attributes}
          {...listeners}
          tabIndex={-1}
          data-testid={`field-tag-drag-handle-${tag.name || "unnamed"}`}
          style={{
            cursor: "grab",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color:
              "color-mix(in oklab, var(--muted-foreground) 80%, transparent)",
            pointerEvents: "auto",
          }}
        >
          <Icon ReactIcon={GripVerticalIcon} size="sm" />
        </div>

        <Popper
          anchor={
            <button
              ref={colorButtonRef}
              className="field-tag-color-swatch"
              data-testid={`field-tag-color-button-${tag.name || "unnamed"}`}
              style={{
                width: 24,
                height: 24,
                borderRadius: "50%",
                border: "1px solid",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 0,
                background: "none",
                ...TagColors?.[tag.color]?.[mode],
              }}
            >
              A
            </button>
          }
          isOpenState={[isColorPopperOpen, setIsColorPopperOpen]}
          isClickable={true}
          placement="bottom-start"
          floatingStyle={{ width: "fit-content" }}
        >
          <ColorGrid
            selectedColor={tag.color}
            onChange={(color) => {
              onColorChange(color, index);
              setIsColorPopperOpen(false);
              colorButtonRef.current?.focus();
            }}
          />
        </Popper>

        <input
          type="text"
          value={tag.name}
          onChange={(e) => onNameChange(e.target.value, index)}
          data-testid={`field-tag-input-${tag.name || "unnamed"}`}
          style={{
            flex: 1,
            border: "none",
            background: "transparent",
            outline: "none",
            ...(typographyStyles["body-md"] as React.CSSProperties),
            color: "var(--foreground)",
          }}
          placeholder="Enter tag name..."
        />

        <button
          className="field-tag-color-swatch"
          onClick={() => onDelete(index)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onDelete(index);
            }
          }}
          data-testid={`field-tag-delete-${tag.name || "unnamed"}`}
          style={{
            border: "1px solid transparent",
            background: "transparent",
            cursor: "pointer",
            padding: "4px",
            borderRadius: "0.25rem",
            color:
              "color-mix(in oklab, var(--muted-foreground) 80%, transparent)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon ReactIcon={Trash2Icon} size="sm" />
        </button>
      </ContentScope>
    </div>
  );
}

function FieldTagValue<T extends FieldDefinitionTagType<T>>({
  value,
  style,
  isBorderVisible = true,
  ref,
}: FieldDefinitionFieldType<FieldDefinitionTagType<T>> & {
  isBorderVisible?: boolean;
  ref?: React.Ref<FieldImperativeHandle>;
}) {
  const text = value?.name?.replace(/_/g, " ");
  const { colorScheme: mode } = useColorScheme();
  const tagRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(
    ref,
    () => ({
      setFocus: () => {
        tagRef.current?.focus();
      },
      unsetFocus: () => {
        tagRef.current?.blur();
      },
    }),
    [],
  );

  const { color, backgroundColor, borderColor } =
    value?.color && mode
      ? TagColors[value.color][mode]
      : {
          color: undefined,
          backgroundColor: undefined,
          borderColor: undefined,
        };

  return (
    <div
      ref={tagRef}
      tabIndex={0}
      data-testid={`field-tag-${text || "empty"}`}
      style={
        {
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "center",
          width: "100%",
          outline: "none",
          // padding: `${theme.spacing(0)} ${theme.spacing(0.5)}`,
          ...style,
        } as React.CSSProperties
      }
    >
      {text && (
        <div
          style={{
            ...(typographyStyles["body-md"] as React.CSSProperties),
            color: color,
            border: isBorderVisible
              ? `1px solid ${borderColor}`
              : "1px solid transparent",
            backgroundColor: backgroundColor,
            borderRadius: "calc(var(--radius) - 4px)",
            maxWidth: "100%",
            width: "fit-content",
            overflow: "hidden",
            textOverflow: "ellipsis",
            alignItems: "baseline",
            flexWrap: "nowrap",
            textWrap: "nowrap",
            height: "100%",
            paddingLeft: "6px",
            paddingRight: "6px",
            paddingTop: "1px",
            paddingBottom: "1px",
          }}
        >
          {typeof text === "string" ? _.capitalize(text) : text}
        </div>
      )}
    </div>
  );
}

function FieldTagsValue<T extends FieldDefinitionTagsType<T>>({
  value,
  valueOptions,
  events,
  style,
  isBorderVisible = true,
  ref,
}: FieldDefinitionFieldType<FieldDefinitionTagsType<T>> & {
  isBorderVisible?: boolean;
  ref?: React.Ref<FieldImperativeHandle>;
}) {
  const tags = value || [];
  const createButtonRef = useRef<HTMLButtonElement>(null);
  const addButtonRef = useRef<HTMLButtonElement>(null);
  const rowNavRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [isPopperOpen, setIsPopperOpenState] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const pendingFocusIndexRef = useRef<number | null>(null);

  const setIsPopperOpen = useCallback(
    (open: boolean) => {
      setIsPopperOpenState(open ?? false);
      if (!open) {
        events?.onSubmitField?.();
      }
    },
    [events],
  );

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = tags.findIndex((item) => item.id === active.id);
      const newIndex = tags.findIndex((item) => item.id === over.id);
      const newTags = arrayMove(tags, oldIndex, newIndex);
      events?.onChangeField?.(newTags);
      events?.onSubmitField?.();
    }
  };

  const handleColorChange = (color: TagColorEnum, index: number) => {
    const newTags = [...tags];
    newTags[index] = { ...newTags[index], color };
    events?.onChangeField?.(newTags);
  };

  const handleDeleteTag = useCallback(
    (index: number) => {
      events?.onChangeField?.(tags.filter((_, i) => i !== index));
      events?.onSubmitField?.();
      // After delete, focus the previous row or the create button
      requestAnimationFrame(() => {
        if (tags.length <= 1) {
          createButtonRef.current?.focus();
        } else if (index > 0) {
          rowNavRefs.current[index - 1]?.focus();
        } else {
          rowNavRefs.current[0]?.focus();
        }
      });
    },
    [events, tags],
  );

  const handleNameChange = (name: string, index: number) => {
    const newTags = [...tags];
    newTags[index] = { ...newTags[index], name };
    events?.onChangeField?.(newTags);
  };

  const handleCreateTag = useCallback(() => {
    const availableColors = Object.values(TagColorEnum);
    const nextColorIndex = tags.length % availableColors.length;

    pendingFocusIndexRef.current = tags.length;
    events?.onChangeField?.([
      ...tags,
      {
        identifier: null,
        id: `temp_${Date.now()}`,
        color: availableColors[nextColorIndex],
        name: "",
      },
    ]);
  }, [events, tags]);

  useEffect(() => {
    if (
      pendingFocusIndexRef.current !== null &&
      pendingFocusIndexRef.current < tags.length
    ) {
      const idx = pendingFocusIndexRef.current;
      pendingFocusIndexRef.current = null;
      requestAnimationFrame(() => {
        rowNavRefs.current[idx]?.focus();
      });
    }
  }, [tags.length]);

  const handleCreateButtonKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "ArrowUp") {
        e.preventDefault();
        e.stopPropagation();
        if (tags.length > 0) {
          rowNavRefs.current[tags.length - 1]?.focus();
        }
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        e.stopPropagation();
        if (tags.length > 0) {
          rowNavRefs.current[0]?.focus();
        }
      } else if (e.key === "Enter") {
        e.preventDefault();
        e.stopPropagation();
        handleCreateTag();
      }
    },
    [tags.length, handleCreateTag],
  );

  useImperativeHandle(
    ref,
    () => ({
      setFocus: () => {
        addButtonRef.current?.focus();
      },
      unsetFocus: () => {
        setIsPopperOpen(false);
        addButtonRef.current?.blur();
      },
    }),
    [],
  );

  const content = (
    <div style={{ padding: "8px", minWidth: 300 }}>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={tags.map((tag) => tag.id)}
          strategy={verticalListSortingStrategy}
        >
          {tags.map((tag, index) => (
            <SortableTag
              key={tag.id + String(index)}
              tag={tag}
              index={index}
              onColorChange={handleColorChange}
              onDelete={handleDeleteTag}
              onNameChange={handleNameChange}
              onArrowUp={() => {
                if (index > 0) {
                  rowNavRefs.current[index - 1]?.focus();
                } else {
                  createButtonRef.current?.focus();
                }
              }}
              onArrowDown={() => {
                if (index < tags.length - 1) {
                  rowNavRefs.current[index + 1]?.focus();
                } else {
                  createButtonRef.current?.focus();
                }
              }}
              navRef={(el) => {
                rowNavRefs.current[index] = el;
              }}
              events={events}
            />
          ))}
        </SortableContext>
      </DndContext>

      <button
        ref={createButtonRef}
        onClick={handleCreateTag}
        onKeyDown={handleCreateButtonKeyDown}
        data-testid="field-tags-create-option"
        style={{
          width: "100%",
          border: "none",
          background: "transparent",
          cursor: "pointer",
          padding: "8px",
          borderRadius: "calc(var(--radius) - 4px)",
          display: "flex",
          alignItems: "center",
          gap: "4px",
          color: "var(--foreground)",
          outline: "none",
          ...(typographyStyles["body-md"] as React.CSSProperties),
        }}
      >
        <Icon ReactIcon={PlusIcon} size="md" />
        Create option
      </button>
    </div>
  );

  return (
    <Popper
      anchor={
        <div
          ref={anchorRef}
          data-testid={`field-tags-${
            tags
              .map((t) => t.name)
              .filter(Boolean)
              .join("-") || "empty"
          }`}
          style={{ display: "flex", alignItems: "center", outline: "none" }}
        >
          <div
            style={
              {
                display: "flex",
                // gap: theme.spacing(1),
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "flex-start",
                overflow: "hidden",
                textOverflow: "ellipsis",
                flexWrap: "nowrap",
                minWidth: "fit-content",
                ...style,
              } as React.CSSProperties
            }
          >
            {tags.map((value, index) => (
              <React.Fragment key={index}>
                {value.name != "" && (
                  <div style={{ padding: `${(value.name ? 0.5 : 0) * 8}px` }}>
                    <FieldTagValue
                      key={index}
                      value={value}
                      valueOptions={valueOptions}
                      isMultiline={false}
                      isBorderVisible={isBorderVisible}
                      style={{ paddingLeft: 0, paddingRight: 0 }}
                    />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
          <Button
            variant="outline"
            size="icon-xs"
            ref={addButtonRef}
            data-testid="field-tags-add-button"
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.stopPropagation();
              }
            }}
            className="m-0.5"
          >
            <Icon ReactIcon={PlusIcon} />
          </Button>
        </div>
      }
      placement="bottom-start"
      isOpenState={[isPopperOpen, setIsPopperOpen]}
      floatingStyle={{ width: "320px" }}
    >
      {content}
    </Popper>
  );
}

function FieldTagSkeleton() {
  return (
    <Skeleton
      variant="text"
      level="body-md"
      style={{
        display: undefined,
        width: "75%",
        maxWidth: Values.DEFAULT_COLUMN_WIDTH,
      }}
    />
  );
}

function FieldTagsSkeleton() {
  return (
    <div
      style={{
        display: undefined,
        width: "75%",
        maxWidth: Values.DEFAULT_COLUMN_WIDTH,
      }}
    >
      <Skeleton
        variant="text"
        level="body-md"
        style={{
          width: "33%",
          maxWidth: Values.DEFAULT_COLUMN_WIDTH,
        }}
      />
      <Skeleton
        variant="text"
        level="body-md"
        style={{
          width: "33%",
          maxWidth: Values.DEFAULT_COLUMN_WIDTH,
        }}
      />
    </div>
  );
}

export function FieldTag<T extends FieldDefinitionTagType<T>>({
  isLoading,
  isBorderVisible = false,
  ref,
  ...props
}: FieldDefinitionFieldType<FieldDefinitionTagType<T>> & {
  isBorderVisible?: boolean;
  ref?: React.Ref<FieldImperativeHandle>;
}) {
  if (isLoading) {
    return <FieldTagSkeleton />;
  }

  return (
    <FieldTagValue {...props} isBorderVisible={isBorderVisible} ref={ref} />
  );
}

export function FieldTags<T extends FieldDefinitionTagsType<T>>({
  isLoading,
  isBorderVisible = false,
  ref,
  ...props
}: FieldDefinitionFieldType<FieldDefinitionTagsType<T>> & {
  isBorderVisible?: boolean;
  ref?: React.Ref<FieldImperativeHandle>;
}) {
  if (isLoading) {
    return <FieldTagsSkeleton />;
  }

  return (
    <FieldTagsValue {...props} isBorderVisible={isBorderVisible} ref={ref} />
  );
}
